import {
  GoogleMapFeatureStyle,
  GoogleMapsFeature,
  GoogleMapsInfoWindowLine,
} from '@sbb-amsgis/google-maps';
import Graphic from '@arcgis/core/Graphic';
import {
  DetailViewForm,
} from '../../../../config/model/detailview-config.model';
import {GoogleMapFeatureConfig, GoogleMapFeatureDynamicStyle} from '../models/google-map-config.model';
import { TranslateService } from '@ngx-translate/core';
import { inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { LayerInfosService } from '../../../../shared/service/layer-infos.service';
import { AgGridListViewService } from '../../../../bottom-panel/services/ag-grid-list-view.service';
import {Observable} from 'rxjs/internal/Observable';

export class GoogleMapsFeatureTransform {
  private readonly translateService: TranslateService;
  private readonly layerInfosService: LayerInfosService;
  private readonly agGridListViewService: AgGridListViewService;

  constructor() {
    this.translateService = inject(TranslateService);
    this.layerInfosService = inject(LayerInfosService);
    this.agGridListViewService = inject(AgGridListViewService);
  }

  transform(
    sbbFeatures: Graphic[],
    config: GoogleMapFeatureConfig,
    detailViewForm: any,
    layerUrl: string
  ): Observable<GoogleMapsFeature[]> {
    // get the styles before mapping the domain values
    const styles = sbbFeatures.map(sbbFeature => this.getStyles(sbbFeature, config));

    return this.mapDomainsOnFeatureList(sbbFeatures, layerUrl).pipe(
      map((features) => {
        return features.map((sbbFeature, idx) => {
          return {
            geometry: sbbFeature.geometry,
            formValues: this.getForm(sbbFeature, config, detailViewForm),
            title: this.getTitle(sbbFeature, detailViewForm),
            id: this.getId(sbbFeature, detailViewForm),
            style: styles[idx]
          };
        });
      })
    );
  }

  private getId(sbbFeature: Graphic, detailViewForm: any): string {
    return `${detailViewForm.fkIdentifyLayerId}_${sbbFeature.attributes['OBJECTID']}`;
  }

  private getTitle(sbbFeature: Graphic, detailViewForm: any): string {
    const attributeTitle = detailViewForm.attributeTitle;
    if (attributeTitle) {
      return sbbFeature.attributes[attributeTitle]?.toString() ?? '';
    }
    return detailViewForm.detailViewTitle ?? '';
  }

  private getForm(sbbFeature: Graphic, config: GoogleMapFeatureConfig, detailViewForm: any): GoogleMapsInfoWindowLine[] {
    // Iterate through the outfields and fill the label/value
    const result: GoogleMapsInfoWindowLine[] = [];

    config.outfields.forEach(fieldName => {
      // exclude Globalid
      if (fieldName === 'GLOBALID') {
        return;
      }

      const fieldConfig = this.findFieldConfig(fieldName, detailViewForm.formFields);
      if (fieldConfig) {
        result.push({
          label: this.translateService.instant(fieldConfig[fieldName] as string),
          value: this.transformValue(sbbFeature, fieldName, fieldConfig),
        });
      }
    });

    return result;
  }

  /* Get the field config from the DetailViewConfig matching by property name */
  private findFieldConfig(fieldName: string, formFields: any[]): any {
    return formFields.find(field =>
      Object.keys(field).some(name => name === fieldName)
    );
  }

  private getStyles(sbbFeature: Graphic, config: GoogleMapFeatureConfig): GoogleMapFeatureStyle {
    const style = config.style;
    const dynamicStyle = this.getDynamicStyle(
      config.dynamicStyles,
      sbbFeature
    );

    return dynamicStyle ?? style;
  }

  private getDynamicStyle(
    dynamicStyles: GoogleMapFeatureDynamicStyle[],
    sbbFeature: Graphic
  ): GoogleMapFeatureStyle {
    if (!dynamicStyles?.length) {
      return null;
    }

    return dynamicStyles.find(
      // simple equals to allow number/string equallity
      // tslint:disable-next-line:triple-equals
      x => sbbFeature.attributes[x.field] == x.value
    );
  }

  private transformValue(sbbFeature: Graphic, fieldName: string, fieldConfig: any): string {
    const value = sbbFeature.attributes[fieldName];

    return value?.toString() ?? '';
  }

  private mapDomainsOnFeatureList(features: Graphic[], layerUrl: string): Observable<Graphic[]> {
    return this.layerInfosService.getLayerInfos(layerUrl).pipe(
      map(layerInfos => {
        features.forEach((feature, index) => {
          features[index] = this.agGridListViewService.matchFeatureAttributeDomain(
            layerInfos.data.fields,
            feature
          );
        });
        return features;
      })
    );
  }
}
